/**
 * writableNavOps — the structural file ops a writable {@link NavSource} exposes.
 *
 * One home for create / rename / delete / move / duplicate so every surface
 * that lets the user edit the tree (the workbench-store nav via
 * {@link useStoreNavSource}, the editable viewer's bespoke nav) delegates to the
 * SAME `store` methods with identical semantics — no divergence in the tricky
 * bits (`move`'s leading-slash convention, `duplicate`'s "… copy" de-dup).
 */
import { renameDocFile } from "../../documents/renameDocFile.js";
import type { WorkspaceStore } from "../WorkspaceStore.js";
import type { NavSource } from "./NavSource.js";

export type WritableNavOps = Pick<
  NavSource,
  "create" | "rename" | "delete" | "move" | "duplicate"
>;

/** `.md` / `.markdown`, case-insensitive — the one markdown law. */
function isMarkdownPath(path: string): boolean {
  return /\.(md|markdown)$/i.test(path);
}

export function writableNavOps(store: WorkspaceStore): WritableNavOps {
  return {
    create: (path, content) => store.createFile(path, content ?? ""),
    // A DOCUMENT rename carries its inbound `[[refs]]` with it. That law lived
    // only in the Documents pane, so renaming a doc from the nav — the primary
    // rename surface — left every reference pointing at a file that no longer
    // exists. Models have no such refs, so they take the plain path.
    rename: (from, to) =>
      void (isMarkdownPath(from) && isMarkdownPath(to)
        ? renameDocFile(store, from, to)
        : store.renameFile(from, to)),
    delete: (path) => store.deleteFile(path),
    // Move = a rename into destDir, preserving the source's leading-slash
    // convention. destDir is a BARE dir path ("" = workspace root).
    move: (from, destDir) => {
      const lead = from.startsWith("/") ? "/" : "";
      const name = from.slice(from.lastIndexOf("/") + 1);
      const dir = destDir.replace(/^\/+|\/+$/g, "");
      const to = lead + (dir ? `${dir}/${name}` : name);
      if (to !== from) store.renameFile(from, to);
    },
    // Duplicate = copy content into a "… copy" sibling (de-duped by suffix).
    duplicate: (path) => {
      const live = store.getState().files;
      const content = live.get(path) ?? "";
      const slashAt = path.lastIndexOf("/");
      const dotAt = path.lastIndexOf(".");
      // Dotfile law (same predicate as the nav's rename fix): a leading dot
      // ("/.gitignore") is part of the NAME, not an extension — without the
      // `+ 1` the stem became "/" and duplicate minted "/ copy.gitignore" (r44).
      const hasExt = dotAt > slashAt + 1;
      const stem = hasExt ? path.slice(0, dotAt) : path;
      const ext = hasExt ? path.slice(dotAt) : "";
      // De-duped against DIRECTORIES as well as files: a directory is not a key
      // in `files`, so `while (live.has(to))` walked straight past one and the
      // store then refused the create — a Duplicate that silently did nothing.
      const taken = (p: string): boolean => {
        const bare = p.replace(/^\/+/, "");
        const ancestors = new Set<string>();
        for (let i = bare.indexOf("/"); i >= 0; i = bare.indexOf("/", i + 1))
          ancestors.add(bare.slice(0, i));
        for (const key of live.keys()) {
          const k = key.replace(/^\/+/, "");
          // Equality and directory-prefix were checked; "an ancestor is a file"
          // was not, so the loop proposed a name the store then refused and the
          // Duplicate silently did nothing.
          if (k === bare || k.startsWith(bare + "/") || ancestors.has(k)) return true;
        }
        return false;
      };
      let to = `${stem} copy${ext}`;
      // When the source's own PARENT is blocked (only reachable from an
      // already-corrupt tree, e.g. a hydrate that bypassed the law), every
      // sibling name is blocked too and a `while (taken)` never returns —
      // freezing the app on a context-menu click. That is a property of the
      // parent, so it is tested once here rather than by capping the counter:
      // a cap gave up at 100 while `a copy 101.arch` was free.
      // The blocking property is "an ancestor is a FILE" — not "the parent
      // exists", which is true of every ordinary directory.
      const underFile = (p: string): boolean => {
        const bare = p.replace(/^\/+/, "");
        for (let i = bare.indexOf("/"); i >= 0; i = bare.indexOf("/", i + 1))
          for (const key of live.keys())
            if (key.replace(/^\/+/, "") === bare.slice(0, i)) return true;
        return false;
      };
      if (underFile(to)) return;
      for (let n = 2; taken(to); n++) to = `${stem} copy ${n}${ext}`;
      store.createFile(to, content);
    },
  };
}
